<template>
  <div class="all-finance-accounts">
    <v-row>
      <v-col cols="12" md="6">
        <div class="text-left ml-5">
          <h2>Finance Accounts</h2>
          <h3 id="total-balance">
            Total Balance:
            {{ Helper.currencyFormat(Helper.totalSum(items, "balance")) }}
          </h3>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <v-row>
          <v-spacer></v-spacer>
          <v-col cols="12" md="4" sm="12" :class="$vuetify.breakpoint.smAndDown ? 'px-0' : 'px-0 text-right'">
            <v-btn @click="createEditDialog.visible = true" color="primary" id="add-account-finance" v-if="currentUser.privileges.canAddFinanceAccounts"
              >Add Account</v-btn
            >
          </v-col>
          <v-col cols="12" md="6" sm="12" class="px-0">
            <v-btn color="primary" id="account-transfer" @click="accountTransferDialog=true">
              Account Transfer
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <IntractDataTable
      class="mr-2"
      title="Accounts"
      :headers="headers"
      :endpoint="endpoints.financeAccounts"
      :export-fields="export_fields"
      :can-export="true"
      @handleClick="handleClick"
      :enable-pagination="false"
      @getAllItemsFunction="(f) => (getAllItemsFunction = f)"
      :filter-fields="{}"
      @updateItems="(serverItems) => (items = serverItems)"
    >
      <template v-slot:item.balance="{ item }">
        <span :class="item.balance >= 0 ? 'green--text' : 'red--text'"
          ><b>{{ Helper.currencyFormat(item.balance) }}</b></span
        >
      </template>
      <template v-slot:item.updated="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on"
              >{{ moment(item.updated).fromNow(true) }} ago</span
            >
          </template>
          <span>{{ moment(item.updated).format("hh:mmA, ll") }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.actions="{ item }">
        <!-- <div >{{ item }}</div> -->
        <div
          v-if="
            currentUser.privileges.canEditFinanceAccounts ||
            currentUser.privileges.canDeleteFinanceAccounts
          "
          class="responsive-icons"
        >
          <v-icon
            size="26"
            class="mr-2 blue--text text--darken-4"
            v-if="currentUser.privileges.canEditFinanceAccounts"
            @click.stop="
              createEditDialog.editId = item.id;
              createEditDialog.visible = true;
            "
          >
            mdi-pencil
          </v-icon>
          <v-icon
            :disabled="item.is_primary"
            v-if="currentUser.privileges.canDeleteFinanceAccounts"
            size="26"
            class="red--text text--darken-2"
            @click.stop="
              showDeleteDialog = true;
              itemToDelete = item;
            "
          >
            mdi-delete
          </v-icon>
        </div>
        <div v-else><strong>- </strong></div>
      </template>
    </IntractDataTable>
    <create-edit-dialog-form
      title="Account"
      :visible="createEditDialog.visible"
      @closeDialog="closeDialog"
      :fields="formFields"
      :endpoint="endpoints.financeAccounts"
      :edit-id="createEditDialog.editId"
      :data-object="createEditDialog.obj"
      :success-message="createEditDialog.successMessage"
      @objectCreated="getAllItemsFunction()"
      @updateObject="(obj) => (createEditDialog.obj = obj)"
    />
    
    <!-- Account transfer dialog -->
    <v-dialog
      v-model="accountTransferDialog"
      persistent
      max-width="600px"
    >
    <v-card>
      <v-card-title class="justify-center">
        <h3> Account Transfer </h3>
      </v-card-title>
      <v-card-text>
        <v-form
          ref="accountTransferForm"
          v-model="submitForm"
        >
          <v-container>
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-autocomplete
                  :items="accounts.filter(x => (!x.restrict_creators || (x.allowed_creators.includes(currentUser.id))) 
                          && (!x.auth_required || x.authorizers.includes(currentUser.id)))"
                  v-model="sourceAccount"
                  item-text="name"
                  return-object
                  label="Source Account*"
                  :hint="sourceAccount.id? 'Available Balance: ' + sourceAccount.balance :''"
                  persistent-hint
                  required
                ></v-autocomplete>
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-autocomplete
                  :items="accounts.filter(x => (x.id != sourceAccount.id && (!x.restrict_creators || x.allowed_creators.includes(currentUser.id))))"
                  v-model="destinationAccount"
                  return-object
                  :disabled="!Object.keys(sourceAccount).length"
                  item-text="name"
                  label="Destination Account*"
                  required
                ></v-autocomplete>
              </v-col>
              <v-col 
                cols="12" 
                sm="6"
              >
                <v-text-field
                  :rules="[rules.required, rules.amount]"
                  label="Transfer Amount*"
                  :disabled="!Object.keys(destinationAccount).length"
                  v-model="transferAmount"
                  :hint="transferAmount? `Remaining Balance after transfer: ${Number(sourceAccount.balance) - Number(transferAmount)} `:''"
                  persistent-hint 
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="6">
                <v-menu
                  ref="menu"
                  v-model="menu"
                  :close-on-content-click="false"
                  :return-value.sync="transactionDate"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <!-- Date-range picker -->
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formattedDate(transactionDate)"
                      label="Date*"
                      :rules="[rules.required]"
                      prepend-icon="mdi-calendar"
                      v-bind="attrs"
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="transactionDate"
                    no-title
                    scrollable
                  >
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="menu = false">
                      Cancel
                    </v-btn>
                    <v-btn
                      text
                      color="primary"
                      @click="
                        $refs.menu.save(transactionDate)
                      "
                    >
                      OK
                    </v-btn>
                  </v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="8">
                <v-select
                  prepend-icon="mdi-credit-card-outline"
                  label="Payment Mode*"
                  :rules="[rules.requiredObject]"
                  v-model="transactionMode"
                  :items="paymentModes.map(x => Object.assign({}, x))"
                  item-value="type"
                  return-object
                  item-text="title"
                ></v-select>
              </v-col>
              <v-col
                cols="12"
                sm="12"
                md="4"
                align-self="center"
                justify="center"
              >
                <v-btn
                  @click="paymentDetailsDialog=true"
                  :disabled="!transactionMode.title"
                >
                  Add Details
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="blue darken-1"
          text
          @click="closeAccountTransferDialog"
        >
          Close
        </v-btn>
        <v-btn
          color="blue darken-1"
          text
          v-model="submitForm"
          :disabled="!submitForm || isLoading"
          @click="accountTransfer()"
        >
          Transfer
        </v-btn>
      </v-card-actions>
    </v-card>
    </v-dialog>

    <!-- Payment Modes dialog -->
      <v-dialog v-model="paymentDetailsDialog" persistent max-width="550px">
        <v-card>
          <v-card-title class="justify-center">
            <span> Add Payment Details</span>
            <v-btn icon absolute top right color="dark" @click="paymentDetailsDialog = false">
              x
            </v-btn>
          </v-card-title>
          <v-card-text class="">
            <v-form ref="paymentDetailsForm">
              <v-container class="mb-3">
                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                    md="6"
                    v-if="transactionMode.type == 1"
                  >
                    <v-text-field
                      label="Cheque Number"
                      required
                      v-model="transactionMode.cheque_number"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="6"
                    v-if="transactionMode.type == 1"
                  >
                    <v-text-field
                      label="Issued to"
                      required
                      v-model="transactionMode.cheque_issued_to"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="6"
                    v-if="transactionMode.type == 2"
                  >
                    <v-text-field
                      label="Issued to"
                      required
                      v-model="transactionMode.dd_issued_to"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    md="6"
                    sm="6"
                    v-if="transactionMode.type == 1 || transactionMode.type == 2"
                  >
                    <v-menu
                      ref="menu2"
                      v-model="menu2"
                      :close-on-content-click="false"
                      :return-value.sync="paymentDate"
                      transition="scale-transition"
                      offset-y
                      max-width="290px"
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="formattedDate(paymentDate)"
                          :label="transactionMode.type == 1 ? 'Cheque Date' : 'DD Date'"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          class=""
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="paymentDate"
                        no-title
                        scrollable
                      >
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="menu2 = false">
                          Cancel
                        </v-btn>
                        <v-btn
                          text
                          color="primary"
                          @click="$refs.menu2.save(paymentDate)"
                        >
                          OK
                        </v-btn>
                      </v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="6"
                    v-if="transactionMode.type != 0"
                  >
                    <v-text-field
                      label="Bank Name"
                      required
                      v-model="transactionMode.bank_name"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="6"
                    v-if="transactionMode.type == 1 || transactionMode.type == 2"
                  >
                    <v-text-field
                      label="Bank Branch"
                      required
                      v-model="transactionMode.bank_branch"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="6"
                    v-if="transactionMode.type == 3"
                  >
                    <v-text-field
                      label="UTR Number"
                      required
                      v-model="transactionMode.utr_number"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="6"
                    v-if="transactionMode.type == 5 || transactionMode.type == 6 || transactionMode.type == 4"
                  >
                    <v-text-field
                      label="Transaction ID"
                      required
                      v-model="transactionMode.transaction_id"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      name="input-3-1"
                      prepend-icon="mdi-comment"
                      rows="1"
                      maxlength="250"
                      counter
                      label="Remarks"
                      auto-grow
                      v-model="transactionMode.remarks"
                    ></v-textarea>
                  </v-col>
                </v-row>
                <v-row
                  class="justify-center pt-0 py-4"
                >
                  <v-col cols="12" md="5" sm="8">
                    <v-btn
                      block
                      color="indigo"
                      class="white--text"
                      @click="paymentDetailsDialog = false"
                    >
                      Save
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </v-card-text>
        </v-card>
      </v-dialog>

    <delete-confirmation-dialog
      title="Are you sure you want to delete this account?"
      serious
      description="Deleting this account will also delete all the transactions linked with this account. This action cannot be undone. Proceed with caution."
      :visible="showDeleteDialog"
      @deleteObject="deleteAccount"
    />
    <v-snackbar :color="snackbar.color" v-model="snackbar.visible">
      {{ snackbar.text }}
      <template>
        <v-btn text @click="snackbar.visible = false"
          >Close</v-btn
        >
      </template>
    </v-snackbar>
  </div>
</template>
<script>
import IntractDataTable from "@components/widgets/generics/IntractDataTable.vue";
import CreateEditDialogForm, {
  CreateEditDialogTypes,
} from "@components/widgets/generics/CreateEditDialogForm.vue";
import Helper from "@utils/misc";
import endpoints from "@api/repository";
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import api from "@api/index";
import DeleteConfirmationDialog from "@components/widgets/dialogs/DeleteConfirmationDialog.vue";
import TourHelper from "@utils/tour_helper";

export default {
  name: "AllFinanceAccounts",
  components: {
    IntractDataTable,
    CreateEditDialogForm,
    DeleteConfirmationDialog,
  },
  data() {
    return {
      items: [],
      endpoints: endpoints,
      moment: moment,
      Helper: Helper,
      getAllItemsFunction: null,
      headers: [
        { text: "Name", value: "name", align: "center" },
        { text: "Account No/ID", value: "account_number", align: "center" },
        { text: "Current Balance", value: "balance", align: "center" },
        { text: "Last Updated", value: "updated", align: "center" },
        { text: "Actions", value: "actions", align: "center" },
      ],

      paymentModes: [
        {
          type: 0,
          title: "Cash"
        },
        {
          type: 1,
          title: "Cheque"
        },
        {
          type: 2,
          title: "DD"
        },
        {
          type: 3,
          title: "NEFT/RTGS"
        },
        {
          type: 4,
          title: "Online/Net Banking"
        },
        {
          type: 5,
          title: "Debit Card"
        },
        {
          type: 6,
          title: "Credit Card"
        }
      ],

      export_fields: {
        "Account Name": "name",
        "Account No/ID": "account_number",
        "Opening Balance": "opening_balance",
        "Current Balance": "current_balance",
        "Created On": {
          field: "created",
          callback: (value) => {
            return this.moment(value).format("hh:mmA, D MMM, YYYY");
          },
        },
        "Last Updated On": {
          field: "updated",
          callback: (value) => {
            return this.moment(value).format("hh:mmA, D MMM, YYYY");
          },
        },
      },
      rules: {
        required: value => !!value || "Required",
        requiredObject: value => Object.keys(value).length > 0 || "Required",
        amount: v => /^[0-9]+(\.\d{1,2})?%?$/.test(v) || "Invalid"
      },
      sourceAccount: {},
      paymentDetailsDialog: false,
      paymentDate: null,
      transactionDate: null,
      menu: false,
      menu2: false,
      destinationAccount: {},
      accounts: [{}],
      transactionMode: {},
      transferAmount: null,
      form: false,
      submitForm: false,
      showDeleteDialog: false,
      accountTransferDialog: false,
      transactionCategoryItems: [],
      itemToDelete: null,
      createEditDialog: {
        successMessage: "Account successfully saved!",
        visible: false,
        obj: {

        },
        editId: null,
      },
      snackbar: {
        visible: false,
        text: "",
        color: "dark",
      },
    };
  },
  computed: {
    ...mapGetters(["accessToken", "isLoading", "currentUser","currentInstitution"]),
    essentials() {
      return {
        accessToken: this.accessToken,
        setLoading: this.setLoading,
        handleErrorsFunction: this.handleApiErrors,
      };
    },

    formFields() {
      return {
        name: {
          fieldType: CreateEditDialogTypes.TEXTFIELD,
          label: "Account Name",
          required: true,
          md: 12,
          max: 255,
        },
        account_number: {
          fieldType: CreateEditDialogTypes.TEXTFIELD,
          label: "Account ID/No.",
          helper: "Unique number for your account",
          md: 6,
        },
        opening_balance: {
          fieldType: CreateEditDialogTypes.TEXTFIELD,
          input: "number",
          label: "Opening Balance",
          helper:
            "This is the balance which will be present in your account as soon it is created",
          md: 6,
          required: true,
          value: 0.0,
          // pattern: /[^0-9.]/,
          prependIcon: "mdi-currency-inr",
        },
        restrict_creators: {
          fieldType: CreateEditDialogTypes.CHECKBOX,
          label: "Restrict Creators?",
          md: 6,
          helper:
            "If enabled, Transactions in this account can only be created by selected staff only.",
        },
        allowed_creators: {
          fieldType: CreateEditDialogTypes.SELECT,
          label: "Who can create transactions?",
          hide: !this.createEditDialog.obj?.restrict_creators,
          helper:
            "Only the users selected in this field will be able to create transactions associated with this account",
          md: 6,
          required: true,
          endpoint: Helper.addUrlParams(endpoints.getAllStaff, "all=true"),
          itemText: "full_name",
          itemValue: "id",
          multiple: true,
        },
        auth_required: {
          fieldType: CreateEditDialogTypes.CHECKBOX,
          label: "Authorization Required?",
          md: 6,
          helper:
            "If enabled, Expenses from this account will need to be authorized by selected staff only.",
        },
        authorizers: {
          fieldType: CreateEditDialogTypes.SELECT,
          value: [],
          label: "Who can Authorize expenses?",
          hide: !this.createEditDialog.obj?.auth_required,
          helper:
            "Only the users selected in this field will be able to authorize expenses happening in this account",
          md: 6,
          required: true,
          endpoint: Helper.addUrlParams(endpoints.getAllStaff, "all=true"),
          itemText: "full_name",
          itemValue: "id",
          multiple: true,
          // valid: () => this.createEditDialog.obj.authorizers.length > 0
          // validate: (val) => val.length > 0
        },
      };
    },
  },
  methods: {
    ...mapActions(["setLoading"]),

    handleTour() {
        var steps = [];
        steps.push({id: '1', title: "Welcome to Finance Accounts", description: "View your various accounts under this section, check whether the account is in <b style='color:green;'>Profit</b> or <b style='color:red;'>Loss</b> by checking their current balance.<br>click on a row to see all transactions related to that account.", videoLink: "https://youtube.com"});
        steps.push({id: '2', description: "View the total balance from all accounts", el: '#total-balance'});
        steps.push({id: '3', description: "Click here to add new account by filling necessary information", position:'left' ,el: '#add-account-finance'});
        steps.push({id: '4', description: "Click here for account transfer by providing amount information.", el: '#account-transfer'});
        TourHelper.startTour(this, "finance__accounts", steps);
    },


    handleClick(account) {
      this.$router.push({
        name: "AllTransactions",
        query: { filter__account: account.id },
      });
    },
    closeDialog() {
      this.createEditDialog.visible = false;
      this.createEditDialog.editId = null;
    },

    formattedDate(date) {
      if (!date) return;
      var formatted = moment(date).format("D MMM, YYYY");
      return formatted;
    },
    // async getAllStaff() {
    //   var url = Helper.addUrlParams(endpoints.getAllStaff, "all=true");
    //   this.allStaff = await api.call(this.essentials, url);

    // },

    async deleteAccount(confirmation) {
      if (!confirmation) {
        this.showDeleteDialog = false;
        this.itemToDelete = null;
        return;
      }
      var response = await api.call(
        this.essentials,
        endpoints.financeAccounts + this.itemToDelete.id + "/",
        api.Methods.DELETE
      );
      this.itemToDelete = null;
      this.showDeleteDialog = false;
      if (response) {
        this.getAllItemsFunction();
        this.snackbar.text = "Account successfully deleted!";
        this.snackbar.color = "success";
        this.snackbar.visible = true;
      }
    },

    async getAllAccounts(){
      var url = endpoints.financeAccounts
			var response = await api.call(this.essentials, url)
			if(response) {
				this.accounts = response
      }
    },

    
    async getCategories() {
      var url = endpoints.financeCategories;
      var response = await api.call(this.essentials, url);
      if (response) {
        this.transactionCategoryItems = response;
      }
    },

    async accountTransfer(){
      // TODO - finish setting up frontend api call and then backend business
      // Create authorized expense for source account and income for destination account
      var url = endpoints.financeAccountTransfer;
      // var receipt_number = await this.getReceiptNumber();

      if (this.transactionMode.type == 1 && this.paymentDate) {
        this.transactionMode.cheque_date = this.paymentDate;
      } else if (this.transactionMode.type == 2 && this.paymentDate) {
        this.transactionMode.dd_date = this.paymentDate;
      }

      var income_category, expense_category;
      
      var val1=this.transactionCategoryItems.find(x => x.code == 'ATI');

      if(!val1){
        this.snackbar.text = 'Account transfer failed due to ATI!';
        this.snackbar.color = "error";
        this.snackbar.visible = true;
        return
      }
      else{
        income_category=val1.id;
      }

      var val2=this.transactionCategoryItems.find(x => x.code == 'ATE');

      if(!val2){
        this.snackbar.text = 'Account transfer failed due to ATE!';
        this.snackbar.color = "error";
        this.snackbar.visible = true;
        return
      } 
      else{
        expense_category=val2.id;
      }
     
      var post_body={
        amount: Number(this.transferAmount),
        source_account: this.sourceAccount.id,
        destination_account: this.destinationAccount.id,
        transaction_date: this.transactionDate,
        manager: this.currentUser.id,
        income_category: income_category,
        expense_category: expense_category,
        payer: this.sourceAccount.name,
        payee: this.destinationAccount.name,
        description: `Account transfer from ${this.sourceAccount.name} to ${this.destinationAccount.name}`,
        transaction_mode: this.transactionMode,
      }

      var response = await api.call(
        this.essentials,
        url,
        api.Methods.POST,
        post_body
      );

      if(response)
      {
        this.getAllItemsFunction()
        this.snackbar.text = 'Account transfer successful!';
        this.snackbar.color = "success";
        this.snackbar.visible = true;
      }

      this.closeAccountTransferDialog();
    },

    closeAccountTransferDialog(){
      this.accountTransferDialog = false;
      this.sourceAccount = {};
      this.destinationAccount = {};
      this.transferAmount = null;
      this.transactionMode = {};
      this.paymentDate = null;
      this.transactionDate = null
      if (this.$refs.paymentDetailsForm) this.$refs.paymentDetailsForm.resetValidation();
      this.$refs.accountTransferForm.resetValidation();
    },
  },

  created(){
    this.getAllAccounts();
    this.handleTour();
    this.getCategories();
  },
};
</script>